import { Alert, Snackbar } from '@mui/material';
import type { BrandInfo, BrandTabConfigItem } from 'api';
import { ReactComponent as CardSVG } from 'assets/icon-card-filled.svg';
import { ReactComponent as GiftSVG } from 'assets/icon-gift-outline.svg';
import { ReactComponent as HomeSVG } from 'assets/icon-home-filled.svg';
import { ReactComponent as MoreSVG } from 'assets/icon-more-outline.svg';
import { ReactComponent as MySVG } from 'assets/icon-my-filled.svg';
import { ReactComponent as StampSVG } from 'assets/icon-stamp-filled.svg';
import { ReactComponent as TicketSVG } from 'assets/icon-ticket-filled.svg';
import { ReactComponent as WalletSVG } from 'assets/icon-wallet.svg';
import MoreMenu from 'components/MoreMenu';
import { useBrand } from 'context/Brand';
import { useLiff } from 'context/Liff';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { splitTabBarItems } from 'utils/tabBarLayout';
import { isPublicTabUrl } from 'utils/tabUrl';

import FormattedMessage from '../FormattedMessage';
import styles from './index.module.scss';

export type MenuItem = {
  id: string;
  icon: (color: string) => ReactNode;
  title: string;
  url?: string;
  onClick?: () => void;
  exact?: boolean;
  hidden?: boolean;
  external?: boolean;
};

const preMenu: MenuItem[] = [
  {
    id: 'coupon',
    icon: (color = '#999') => <TicketSVG fill={color} />,
    title: FormattedMessage('components.MenuTabBar.menu_item.ticket'),
    url: '/pre/coupon',
    exact: true,
  },
  {
    id: 'my',
    icon: (color = '#999') => <MySVG fill={color} />,
    title: FormattedMessage('components.MenuTabBar.menu_item.my'),
    url: '/pre/me',
    exact: true,
  },
];

const ConfiguredTabIcon: FC<{
  fallback: ReactNode;
  iconUrl: string;
}> = ({ fallback, iconUrl }) => {
  const [failed, setFailed] = useState(false);

  // A tab configuration may refresh without unmounting this component. Retry a
  // newly uploaded icon instead of retaining the failure state of the old URL.
  useEffect(() => {
    setFailed(false);
  }, [iconUrl]);

  if (failed) {
    return <>{fallback}</>;
  }

  return (
    <img
      key={iconUrl}
      className={styles.customIcon}
      src={iconUrl}
      alt=""
      onError={() => setFailed(true)}
    />
  );
};

const defaultSystemMenu = (
  menuId: string,
  brandInfo?: BrandInfo,
  isOaPlus?: boolean,
): MenuItem => {
  switch (menuId) {
    case 'home':
      return {
        id: menuId,
        icon: (color = '#999') => <HomeSVG fill={color} />,
        title: FormattedMessage('components.MenuTabBar.menu_item.home'),
        url: '/home',
      };
    case 'member_card':
      return {
        id: menuId,
        icon: (color = '#999') => <CardSVG fill={color} />,
        title: FormattedMessage('components.MenuTabBar.menu_item.member_card'),
        url: '/membercard',
        exact: true,
      };
    case 'loyalty_card':
      return {
        id: menuId,
        icon: (color = '#999') => <StampSVG fill={color} />,
        title: FormattedMessage('components.MenuTabBar.menu_item.loyalty_card'),
        url: '/pointcards',
        exact: true,
      };
    case 'coupon':
      return {
        id: menuId,
        icon: (color = '#999') => <TicketSVG fill={color} />,
        title: FormattedMessage('components.MenuTabBar.menu_item.coupon'),
        url: '/coupon',
        exact: true,
      };
    case 'store':
      return {
        id: menuId,
        icon: (color = '#999') =>
          isOaPlus ? <WalletSVG fill={color} /> : <GiftSVG fill={color} />,
        title: isOaPlus
          ? FormattedMessage('components.MenuTabBar.menu_item.store_value_card')
          : FormattedMessage('components.MenuTabBar.menu_item.store'),
        url: isOaPlus ? '/store_value' : '/store',
        exact: true,
        hidden: isOaPlus && brandInfo?.brand_modules?.store !== 1,
      };
    case 'member_area':
      return {
        id: menuId,
        icon: (color = '#999') => <MySVG fill={color} />,
        title: FormattedMessage('components.MenuTabBar.menu_item.member_area'),
        url: '/memberarea',
        exact: true,
      };
    default:
      return { id: menuId, icon: () => <MoreSVG />, title: '' };
  }
};

const configToMenuItem = (
  tab: BrandTabConfigItem,
  brandInfo: BrandInfo,
  isOaPlus: boolean,
  onOpenExternal: (tab: BrandTabConfigItem) => void,
): MenuItem => {
  const systemKey = tab.system_key || tab.internal_target || '';
  const base = defaultSystemMenu(systemKey, brandInfo, isOaPlus);
  const iconUrl = tab.icon_url;
  const icon = iconUrl
    ? (color = '#999') => (
        <ConfiguredTabIcon fallback={base.icon(color)} iconUrl={iconUrl} />
      )
    : base.icon;
  const title = tab.display_name || base.title;
  const id = `tab-${tab.id ?? tab.system_key ?? title}`;

  if (tab.link_type === 'external') {
    return {
      id,
      icon,
      title,
      external: true,
      onClick: () => onOpenExternal(tab),
    };
  }
  if (tab.link_type === 'loyalty_card' && tab.loyalty_card_id) {
    return {
      id,
      icon,
      title,
      url: `/pointcard/${tab.loyalty_card_id}`,
      exact: true,
    };
  }

  return { ...base, id, icon, title };
};

type MenuTabBarProps = { pre?: boolean };

const MenuTabBar: FC<MenuTabBarProps> = ({ pre }) => {
  const { brandInfo } = useBrand();
  const { liff } = useLiff();
  const [visible, setVisible] = useState(false);
  const [externalOpenError, setExternalOpenError] = useState(false);
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [moreMenu, setMoreMenu] = useState<MenuItem[]>([]);
  const isOaPlus = brandInfo?.identifier.includes('@') || false;

  const openExternalTab = (tab: BrandTabConfigItem) => {
    if (!isPublicTabUrl(tab.external_url)) {
      setExternalOpenError(true);
      return;
    }
    try {
      Promise.resolve(
        liff.openWindow({
          url: tab.external_url,
          external: tab.open_method === 'external_browser',
        }),
      ).catch(() => setExternalOpenError(true));
    } catch (error) {
      console.error('Unable to open tab external URL', error);
      setExternalOpenError(true);
    }
  };

  useEffect(() => {
    if (!brandInfo) return;
    const configuredTabs = brandInfo.tabs;
    const tabs = configuredTabs
      ? configuredTabs.map((tab) =>
          configToMenuItem(tab, brandInfo, isOaPlus, openExternalTab),
        )
      : brandInfo.menu.map((legacy) =>
          defaultSystemMenu(legacy.id, brandInfo, isOaPlus),
        );
    const available = tabs.filter((item) => !item.hidden);
    const safeAvailable = available.length
      ? available
      : [defaultSystemMenu('home', brandInfo, isOaPlus)];

    const { primary, overflow } = splitTabBarItems(safeAvailable);
    if (overflow.length > 0) {
      setMenu([
        ...primary,
        {
          id: 'more',
          icon: (color = '#999') => <MoreSVG fill={color} />,
          title: FormattedMessage('components.MenuTabBar.menu_item.more'),
          onClick: () => setVisible(true),
        },
      ]);
      setMoreMenu(overflow);
      return;
    }

    setMenu(primary);
    setMoreMenu([]);
  }, [brandInfo, isOaPlus]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderMenuItem = (menuItem: MenuItem) => {
    if (menuItem.id === 'more' || menuItem.external) {
      return (
        <button
          type="button"
          onClick={menuItem.onClick}
          className={styles.menuItem}
          key={menuItem.id}
        >
          <div>{menuItem.icon('#999')}</div>
          <span>{menuItem.title}</span>
        </button>
      );
    }
    return (
      <NavLink
        to={menuItem.url || '/home'}
        className={({ isActive }) =>
          `${styles.menuItem}${isActive ? ` ${styles.active}` : ''}`
        }
        key={menuItem.id}
      >
        <div>{menuItem.icon('#999')}</div>
        <span>{menuItem.title}</span>
      </NavLink>
    );
  };

  return (
    <>
      <div className={styles.menuTabBar}>
        {pre ? preMenu.map(renderMenuItem) : menu.map(renderMenuItem)}
      </div>
      <MoreMenu
        visible={visible}
        toggleVisible={setVisible}
        moreMenu={moreMenu}
      />
      <Snackbar
        open={externalOpenError}
        autoHideDuration={4000}
        onClose={() => setExternalOpenError(false)}
      >
        <Alert severity="error" onClose={() => setExternalOpenError(false)}>
          {FormattedMessage('components.MenuTabBar.external_open_failed')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MenuTabBar;
